/**
 * Signup screen — RHF + Zod, POST /auth/signup.
 *
 * CONTRACT (unchanged — SignupScreen.test.tsx covers every line of it):
 *   - Fields: email + password + language (RU/KY/EN per ADR-028).
 *   - On 201: navigate to `/verify-email?user_id=…&email=…` (plus `dev_otp`
 *     outside production, where no mail is actually delivered).
 *   - On 409 `auth.signup.email_exists`: inline error on the email field.
 *   - On 422 `password.breached` / `password.too_short`: inline error on the
 *     password field.
 *   - Optional `?email=` prefill, written by the login screen's
 *     unverified-recovery link.
 *   - OAuth Google under the form. (Facebook was removed 2026-08-26.)
 *
 * ── The counter rebuild (2026-08) ────────────────────────────────────────────
 *
 * What was here was the generated-signup-page template in full: a primary-soft
 * icon tile, an uppercase "Добро пожаловать в GardEn" badge above the heading,
 * a bold heading, a grey lede, three boxed inputs. The badge was a kicker — the
 * one form this rebuild bans outright — and the icon disc said nothing the
 * heading did not. Both are gone; the heading speaks, and the form became a
 * ruled slip (see `components/slip.tsx`).
 *
 * THE PASSWORD RULE IS NOW VISIBLE WHILE YOU TYPE. The server enforces exactly
 * two things: 12 characters minimum, and absence from the HIBP breach corpus.
 * The field shows a live `n/12` counter in the instrument face and states the
 * breach rule as helper text — no strength bar, because a strength bar would be
 * inventing a policy the server does not have and lecturing an already-anxious
 * first-time user about it. Being told "weak" after the fact is the thing this
 * audience least needs.
 *
 * The language field stays a native `<select>`: it is the account's stored
 * `preferred_language`, it must be a real form control the browser and
 * assistive tech understand, and on Android it opens the OS picker. Because it
 * IS the language control for this screen, the shell's `LanguageSwitcher` is
 * suppressed here — two controls for one setting on one page is worse than a
 * small difference in chrome.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, OctagonAlert } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Stagger, StaggerItem } from "@/components/motion";
import { AuthCounter } from "@/features/auth/components/AuthCounter";
import {
  AUTH_INLINE_LINK_CLASS,
  AUTH_LINK_CLASS,
  AuthDivider,
  AuthSubmit,
} from "@/features/auth/components/controls";
import { OAuthButtons } from "@/features/auth/components/OAuthButtons";
import { PasswordField } from "@/features/auth/components/PasswordField";
import {
  SLIP_INPUT_CLASS,
  Slip,
  SlipField,
  SlipNotice,
} from "@/features/auth/components/slip";
import { safeReturnToOrNull, withReturnTo } from "@/features/auth/destination";
import {
  MIN_PASSWORD_LENGTH,
  signupDefaultValues,
  signupSchema,
  UI_LANGUAGES,
  type SignupFormValues,
  type UiLanguage,
} from "@/features/auth/schemas";
import { ApiError, NetworkError, apiClient, AUTH_PATHS } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { iconProps } from "@/lib/icons";
import { useUiStore } from "@/lib/ui-store";

interface SignupResponse {
  user_id: string;
  email: string;
  email_pending_verification: boolean;
  pending_verification: boolean;
  /** NON-PROD only — echoed plaintext OTP (QA has no real email delivery). */
  dev_otp?: string | null;
}

/**
 * Localized-error mapping per AC + Implementation logic step 2.
 * Returns the field to flag plus its i18n key. `retryable` marks the failures
 * where pressing the same button again is a plausible fix, so the root panel
 * can offer a retry instead of ending in a dead end.
 */
function mapSignupError(err: unknown): {
  field: "email" | "password" | "root";
  key: string;
  retryable: boolean;
} {
  if (err instanceof NetworkError) {
    return { field: "root", key: "errors.network", retryable: true };
  }
  if (!(err instanceof ApiError)) {
    return { field: "root", key: "errors.generic", retryable: true };
  }
  switch (err.code) {
    case "auth.signup.email_exists":
      return {
        field: "email",
        key: "errors.auth.signup.email_exists",
        retryable: false,
      };
    case "password.breached":
      return {
        field: "password",
        key: "errors.auth.password.breached",
        retryable: false,
      };
    case "password.too_short":
      return {
        field: "password",
        key: "errors.auth.password.too_short",
        retryable: false,
      };
    case "password.hibp_unavailable":
      return {
        field: "root",
        key: "errors.auth.password.hibp_unavailable",
        retryable: true,
      };
    case "auth.signup.invalid_input":
      return {
        field: "root",
        key: "errors.auth.signup.invalid_input",
        retryable: false,
      };
    default:
      return { field: "root", key: "errors.generic", retryable: true };
  }
}

export function SignupScreen(): ReactElement {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const uiLanguage = useUiStore((s) => s.language);
  const [serverError, setServerError] = useState<{
    key: string;
    retryable: boolean;
  } | null>(null);

  // Optional `?email=…` prefill — set by the login screen's unverified-recovery
  // link when the user can't get past /login because their email is
  // unverified. The signup flow's existing-but-unverified branch
  // (STORY-001-002) idempotently re-issues an OTP for the same email.
  const prefilledEmail = searchParams.get("email") ?? "";
  const rawReturnTo = searchParams.get("return_to");

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      ...signupDefaultValues,
      email: prefilledEmail,
      language: uiLanguage,
    },
    mode: "onBlur",
  });

  // Keep the email field in sync if the query param changes after
  // mount (e.g. router-driven navigation while the screen is alive).
  // `form.setValue` is stable from RHF so it's safe in the deps array.
  const { setValue } = form;
  useEffect(() => {
    if (prefilledEmail !== "") {
      setValue("email", prefilledEmail);
    }
  }, [prefilledEmail, setValue]);

  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = async (values: SignupFormValues): Promise<void> => {
    setServerError(null);
    try {
      const data = await apiClient.post<SignupResponse, SignupFormValues>(
        AUTH_PATHS.signup,
        values,
        { skipRefreshOn401: true },
      );
      // Sync the selected language to the UI store so the verify-email
      // screen renders in the chosen language.
      setLanguage(values.language);
      // The 201 response carries no access_token (server issues it
      // post-OTP-verify); make sure the in-memory token is null so
      // the verify-email screen knows we're still pre-auth.
      setAccessToken(null);
      const search = new URLSearchParams({
        user_id: data.user_id,
        email: data.email,
      });
      // NON-PROD: carry the echoed OTP so /verify-email can show it on-screen
      // (QA has no real email delivery). Always absent in production.
      if (data.dev_otp != null && data.dev_otp !== "") {
        search.set("dev_otp", data.dev_otp);
      }
      // W0.1: the code screen needs the destination too — it is where the
      // session is actually issued.
      const safeReturnTo = safeReturnToOrNull(rawReturnTo);
      if (safeReturnTo !== null) {
        search.set("return_to", safeReturnTo);
      }
      navigate("/verify-email?" + search.toString(), { replace: true });
    } catch (err) {
      const mapped = mapSignupError(err);
      if (mapped.field === "email" || mapped.field === "password") {
        form.setError(mapped.field, { message: mapped.key });
        // Also focus the offending field so SR users hear the error.
        form.setFocus(mapped.field);
      } else {
        setServerError({ key: mapped.key, retryable: mapped.retryable });
      }
    }
  };

  const submit = form.handleSubmit(onSubmit);
  const emailErrorKey = form.formState.errors.email?.message;
  const passwordErrorKey = form.formState.errors.password?.message;
  const passwordValue = form.watch("password");

  return (
    <AuthCounter
      door="consumer"
      doorName={t("signup.doorName")}
      title={t("signup.title")}
      lede={t("signup.lede")}
      testId="signup-screen"
      showLanguageSwitcher={false}
    >
      <Stagger className="gav2-stack-lg flex flex-col">
        <StaggerItem>
          <form
            className="gav2-stack-md flex flex-col"
            onSubmit={submit}
            noValidate
            aria-describedby={serverError !== null ? "signup-server-error" : undefined}
          >
            <Slip>
              <SlipField
                htmlFor="signup-email"
                label={t("signup.emailLabel")}
                required
                requiredLabel={t("common.requiredField")}
                error={emailErrorKey !== undefined ? t(emailErrorKey) : undefined}
                errorId="signup-email-error"
                errorTestId="signup-email-error"
              >
                <input
                  id="signup-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  aria-required="true"
                  aria-invalid={emailErrorKey !== undefined || undefined}
                  aria-describedby={
                    emailErrorKey !== undefined ? "signup-email-error" : undefined
                  }
                  placeholder={t("signup.emailPlaceholder")}
                  disabled={isSubmitting}
                  data-testid="signup-email"
                  className={SLIP_INPUT_CLASS}
                  {...form.register("email")}
                />
              </SlipField>

              <PasswordField
                id="signup-password"
                label={t("signup.passwordLabel")}
                autoComplete="new-password"
                placeholder={t("signup.passwordPlaceholder")}
                disabled={isSubmitting}
                data-testid="signup-password"
                hint={t("signup.passwordHint")}
                hintId="signup-password-hint"
                aria-describedby={
                  passwordErrorKey !== undefined
                    ? "signup-password-error signup-password-hint"
                    : "signup-password-hint"
                }
                error={passwordErrorKey !== undefined ? t(passwordErrorKey) : undefined}
                errorId="signup-password-error"
                errorTestId="signup-password-error"
                valueLength={passwordValue.length}
                minLength={MIN_PASSWORD_LENGTH}
                {...form.register("password")}
              />

              <SlipField
                htmlFor="signup-language"
                label={t("signup.languageLabel")}
                required
                requiredLabel={t("common.requiredField")}
              >
                <div className="relative w-full">
                  <select
                    id="signup-language"
                    aria-required="true"
                    disabled={isSubmitting}
                    data-testid="signup-language"
                    className={
                      SLIP_INPUT_CLASS + " cursor-pointer appearance-none pr-8"
                    }
                    {...form.register("language")}
                    onChange={(e) => {
                      const next = e.target.value as UiLanguage;
                      form.setValue("language", next, { shouldDirty: true });
                      setLanguage(next);
                    }}
                  >
                    {UI_LANGUAGES.map((lng) => (
                      <option key={lng} value={lng}>
                        {t("lang." + lng)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    {...iconProps("sm")}
                    className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[var(--g-ink-2)]"
                  />
                </div>
              </SlipField>
            </Slip>

            {serverError !== null ? (
              <SlipNotice
                tone="error"
                role="alert"
                id="signup-server-error"
                icon={<OctagonAlert {...iconProps("sm")} />}
              >
                <p data-testid="signup-server-error">{t(serverError.key)}</p>
                {serverError.retryable ? (
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={isSubmitting}
                    data-testid="signup-error-retry"
                    className={"mt-1 " + AUTH_LINK_CLASS}
                  >
                    {t("common.retry")}
                  </button>
                ) : null}
              </SlipNotice>
            ) : null}

            <AuthSubmit
              pending={isSubmitting}
              pendingLabel={t("signup.submitting")}
              testId="signup-submit"
            >
              {t("signup.submit")}
            </AuthSubmit>
          </form>
        </StaggerItem>

        <StaggerItem className="gav2-stack-md flex flex-col">
          <AuthDivider label={t("signup.orDivider")} />
          <OAuthButtons
            returnTo={safeReturnToOrNull(rawReturnTo) ?? "/chat"}
            disabled={isSubmitting}
          />
        </StaggerItem>

        <StaggerItem className="text-center text-sm text-[var(--g-ink-2)]">
          {t("signup.haveAccount")}{" "}
          <Link
            to={withReturnTo("/login", rawReturnTo)}
            className={AUTH_INLINE_LINK_CLASS}
            data-testid="signup-haveaccount-link"
          >
            {t("signup.haveAccountLink")}
          </Link>
        </StaggerItem>
      </Stagger>
    </AuthCounter>
  );
}

export default SignupScreen;
